import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { growaveConfigurationToken, modalManagerToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import QuestionsQuery from "@apps/reviews/queries/QuestionsQuery";
import QuestionsService from "@apps/reviews/services/QuestionsService";
import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
import QuestionsStore from "@apps/reviews/stores/QuestionsStore";
import {
    reviewsApplicationLoggerToken,
    reviewsAuthApiClientToken,
} from "@apps/reviews/tokens";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
import { I18nModule } from "@modules/i18n/I18nModule";
import { container, injectable } from "tsyringe";

import { ModalManager } from "@components/Modal/ModalManager";
import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import QuestionsListWidgetComponent from "./QuestionsListWidget.svelte";
import { QuestionsListWidgetConfigInterface } from "./QuestionsListWidgetConfig";

type QuestionsListWidgetProps = {
    showAddQuestionButton?: boolean;
};

@injectable()
class QuestionsListWidget implements WidgetInterface {
    private readonly questionsQuery: QuestionsQuery;
    private readonly questionsService: QuestionsService;
    private readonly reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    private readonly modalManager: ModalManager;
    private readonly i18nModule: I18nModule;
    private readonly showAddQuestionButton: boolean;

    constructor(
        private readonly config: QuestionsListWidgetConfigInterface,
        private readonly logger: LoggerInterface,
        private readonly productIdManager: ProductIdManagerInterface,
        questionsService?: QuestionsService,
        questionsQuery?: QuestionsQuery,
        props?: QuestionsListWidgetProps
    ) {
        this.logger.debug("QuestionsListWidget is initialized", config);

        this.reviewsStorefrontValuesService = container.resolve(
            ReviewsStorefrontValuesService
        );
        this.showAddQuestionButton = props?.showAddQuestionButton ?? true;

        if (questionsQuery && questionsService) {
            this.questionsQuery = questionsQuery;
            this.questionsService = questionsService;
        } else {
            const questionsStore = new QuestionsStore();
            this.questionsQuery = new QuestionsQuery(questionsStore);
            this.questionsService = new QuestionsService(
                questionsStore,
                container.resolve(reviewsAuthApiClientToken),
                container.resolve(reviewsApplicationLoggerToken)
            );
        }

        this.modalManager = container.resolve(modalManagerToken);

        this.i18nModule = container.resolve(I18nModule);
    }
    async init(): Promise<this> {
        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );

        const element = this.config.target;

        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        // I18n
        const i18nModule = container.resolve(I18nModule);
        i18nModule.exports.i18n.registerLoader(
            "reviews.ReviewsProductPageWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "reviews.ReviewsProductPageWidget"
            )
        );
        i18nModule.exports.i18n.registerLoader(
            "reviews.critical.ReviewsProductPageWidget",
            i18nModule.exports.i18nCriticalLoaderFactory.getLoader(
                "reviews.critical.ReviewsProductPageWidget"
            )
        );

        // inject Customizations
        const customizationManager = container.resolve(CustomizationManager);
        const customizationManagerConfig = {
            widgetName: REVIEWS_WIDGET_NAMES.QUESTIONS_LIST_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromDesignMode({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        new QuestionsListWidgetComponent({
            target: shadowRoot || element,
            props: {
                reviewsStorefrontValuesService:
                    this.reviewsStorefrontValuesService,
                questionsQuery: this.questionsQuery,
                questionsService: this.questionsService,
                productIdManager: this.productIdManager,
                i18nModule: this.i18nModule,
                modalManager: this.modalManager,
                showAddQuestionButton: this.showAddQuestionButton,
                isNestedWidget: !this.config.isWidgetShadowRootEnabled,
                afterWidgetInject: () =>
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    ),
            },
        });

        return this;
    }

    destroy() {
        // pass
    }
}

export type QuestionsListWidgetInterface = typeof QuestionsListWidget;

export default QuestionsListWidget;
